import React, { useState } from 'react';
import { Mail, Phone, MapPin, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { contactAPI } from '@/utils/api';
import ObfuscatedEmail from '@/components/ObfuscatedEmail';
import SEOHead from '@/components/SEOHead';
import { useLanguagePrefix } from '@/hooks/useLanguagePrefix';

const ContactPage = () => {
  const { t, i18n } = useTranslation();
  const langPrefix = useLanguagePrefix();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    language: i18n.language || 'en', // Include language in form data
    honeypot: '' // Hidden field for spam protection
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Ensure language is set in form data
      const submitData = { ...formData, language: i18n.language || 'en' };
      await contactAPI.submit(submitData);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        message: '',
        language: i18n.language || 'en',
        honeypot: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (err) {
      setError(t('contact.form.error'));
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <SEOHead 
        title={t('seo.contact.title')}
        description={t('seo.contact.description')}
        keywords="gold trading API, tokenization API, institutional gold platform, ERC-3643, Fireblocks custody, government vault storage, third-party attestations, fully audited, low-cost gold trading, RWA, real world assets"
        canonical={`${langPrefix}/contact`}
      />
      
      {/* Hero Section */}
      <section className="bg-kitco-navy text-white py-20" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4" data-testid="hero-title">{t('contact.hero.title')}</h1>
          <p className="text-xl text-grey-300" data-testid="hero-subtitle">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20" data-testid="contact-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Institutional Notice */}
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="bg-amber-50 border-l-4 border-kitco-gold p-6 rounded-lg">
              <h3 className="text-lg font-bold text-kitco-navy mb-3">{t('contact.note.title')}</h3>
              <p className="text-grey-600">
                {t('contact.note.description')} <a href="https://online.kitco.com" target="_blank" rel="noopener noreferrer" className="text-kitco-gold-hover hover:text-amber-700 font-semibold underline">online.kitco.com</a>
              </p>
            </div>
          </div>

          {/* Contact Form */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Traditional Contact Form */}
              <div>
              <Card data-testid="contact-form-card">
                <CardHeader>
                  <CardTitle className="text-2xl" data-testid="form-title">{t('contact.title')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {submitSuccess && (
                    <div className="mb-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-400 rounded-xl shadow-lg animate-fade-in" data-testid="success-message">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <CheckCircle2 className="h-8 w-8 text-green-600 animate-bounce" />
                        </div>
                        <div className="ml-4 flex-1">
                          <h3 className="text-lg font-bold text-green-900 mb-1">{t('contact.form.success')}</h3>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {error && (
                    <div className="mb-6 p-5 bg-red-50 border-2 border-red-300 rounded-xl shadow-md" data-testid="error-message">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <svg className="h-6 w-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-red-800 font-medium">{error}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Honeypot field - hidden from users */}
                    <input
                      type="text"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleChange}
                      style={{ display: 'none' }}
                      tabIndex="-1"
                      autoComplete="off"
                      data-testid="honeypot-field"
                    />

                    <div>
                      <Label htmlFor="name" data-testid="name-label">{t('contact.form.name')} *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" data-testid="email-label">{t('contact.form.email')} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="email-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="company" data-testid="company-label">{t('contact.form.company')} *</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="company-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" data-testid="phone-label">{t('contact.form.phone')} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-1"
                        data-testid="phone-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" data-testid="message-label">{t('contact.form.message')} *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={t('contact.form.messagePlaceholder')}
                        required
                        rows={6}
                        className="mt-1"
                        data-testid="message-textarea"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-kitco-gold hover:bg-kitco-gold-hover text-black font-semibold"
                      data-testid="submit-button"
                    >
                      {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                    </Button>
                    
                    <p className="text-xs text-grey-500 text-center mt-4">
                      {t('contact.disclaimer')}
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-kitco-navy mb-8" data-testid="contact-info-title">{t('contact.info.title')}</h2>
              </div>

              <Card data-testid="email-card">
                <CardContent className="p-6 flex items-start">
                  <Mail className="h-6 w-6 text-kitco-gold mr-4 mt-1" />
                  <div>
                    <h3 className="font-semibold text-kitco-navy mb-2" data-testid="email-title">{t('contact.info.email')}</h3>
                    <ObfuscatedEmail 
                      user="info" 
                      domain="kitcodigitalmetals.com" 
                      className="text-grey-500 hover:text-kitco-gold-hover" 
                      data-testid="email-link"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="phone-card">
                <CardContent className="p-6 flex items-start">
                  <Phone className="h-6 w-6 text-kitco-gold mr-4 mt-1" />
                  <div>
                    <h3 className="font-semibold text-kitco-navy mb-2" data-testid="phone-title">{t('contact.info.phone')}</h3>
                    <p className="text-grey-500">
                      <a href="tel:18339722424" className="block hover:text-kitco-gold-hover" data-testid="phone-us-link">
                        1-833-972-2424 (USA & Canada)
                      </a>
                      <a href="tel:5148001471" className="block hover:text-kitco-gold-hover" data-testid="phone-intl-link">
                        514-800-1471 (Worldwide)
                      </a>
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card data-testid="offices-card">
                <CardContent className="p-6 flex items-start">
                  <MapPin className="h-6 w-6 text-kitco-gold mr-4 mt-1" />
                  <div>
                    <h3 className="font-semibold text-kitco-navy mb-2" data-testid="offices-title">{t('contact.info.offices')}</h3>
                    <p className="text-grey-500" data-testid="offices-content">
                      {t('contact.info.montreal')}<br />
                      {t('contact.info.newyork')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;